<template>
  <b-modal
    id="SwapManagerDialog"
    ref="SwapManagerDialog"
    title="โอนย้ายผู้จัดการสาขา"
    centered
    no-close-on-backdrop
    @hide="onHide"
    @ok.prevent="submit"
  >
    <div class="info-card mb-3">
      <i class="fas fa-info-circle mr-2"></i>
      <span>
        สาขาต้องมีผู้จัดการตลอดเวลา ระบบจะ
        <strong>มอบบทบาทให้คนใหม่ก่อน แล้วจึงถอดคนเดิม</strong>
        ในลำดับเดียวกัน (atomic)
      </span>
    </div>

    <div v-if="currentManager" class="mb-3">
      <div class="text-muted small mb-1">ผู้จัดการคนเดิม</div>
      <div class="manager-card outgoing">
        <div>
          <strong>
            {{ currentManager.firstNameTh }} {{ currentManager.lastNameTh }}
          </strong>
          <span v-if="branchLabel" class="text-muted small">
            · สาขา {{ branchLabel }}
          </span>
        </div>
        <b-badge variant="info">{{ roleLabel('MANAGER') }}</b-badge>
      </div>
    </div>

    <b-form-group label="เลือกผู้จัดการคนใหม่" label-class="font-weight-bold">
      <div v-if="candidates.length === 0" class="text-danger small">
        ไม่มีผู้ใช้คนอื่นในสาขานี้ที่สามารถรับหน้าที่ผู้จัดการได้
      </div>
      <div v-else class="candidate-list">
        <button
          v-for="c in candidates"
          :key="c.uid"
          type="button"
          class="candidate-card"
          :class="{ selected: newUid === c.uid }"
          @click="newUid = c.uid"
        >
          <span class="role-radio" :class="{ on: newUid === c.uid }"></span>
          <span class="candidate-body">
            <span class="font-weight-bold">
              {{ c.firstNameTh }} {{ c.lastNameTh }}
            </span>
            <span class="candidate-roles">
              <b-badge
                v-for="rk in (c.roleKeys || [])"
                :key="rk"
                variant="light"
                class="mr-1"
              >
                {{ roleLabel(rk) }}
              </b-badge>
              <span v-if="!(c.roleKeys || []).length" class="text-muted small">
                ยังไม่มีบทบาทในสาขานี้
              </span>
            </span>
          </span>
        </button>
      </div>
    </b-form-group>

    <b-form-group
      label="เหตุผล (อย่างน้อย 10 ตัวอักษร — ใช้กับทั้ง 2 ขั้นตอน)"
      label-class="font-weight-bold"
      :invalid-feedback="reasonError"
      :state="reasonState"
    >
      <b-form-textarea
        v-model="reason"
        rows="3"
        :state="reasonState"
        placeholder="ระบุเหตุผลในการโอนย้ายผู้จัดการ..."
      />
    </b-form-group>

    <b-alert v-if="errorMessage" show variant="danger" class="mt-2 mb-0">
      {{ errorMessage }}
    </b-alert>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel">
        ยกเลิก
      </b-button>
      <b-button
        variant="primary"
        :disabled="!canSubmit || isSubmitting"
        @click="ok"
      >
        <b-spinner v-if="isSubmitting" small class="mr-1" />
        ยืนยันการโอนย้าย
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios";
import { ROLE_LABELS_TH } from "@/constants/roles";

export default {
  name: "SwapManagerDialog",
  data() {
    return {
      clinicUrl: null,
      branchUrl: null,
      branchLabel: null,
      currentManager: null,
      candidates: [],
      newUid: null,
      reason: "",
      isSubmitting: false,
      errorMessage: "",
    };
  },
  computed: {
    reasonState() {
      if (this.reason.length === 0) return null;
      return this.reason.trim().length >= 10;
    },
    reasonError() {
      if (this.reason.length > 0 && this.reason.trim().length < 10) {
        return "กรุณาระบุเหตุผลอย่างน้อย 10 ตัวอักษร";
      }
      return "";
    },
    canSubmit() {
      return (
        this.newUid &&
        this.currentManager &&
        this.reason.trim().length >= 10
      );
    },
  },
  methods: {
    roleLabel(rk) {
      return ROLE_LABELS_TH[rk] || rk;
    },
    show(payload) {
      // payload: { clinicUrl, branchUrl, branchLabel?, currentManager, candidates }
      this.clinicUrl = payload.clinicUrl;
      this.branchUrl = payload.branchUrl;
      this.branchLabel = payload.branchLabel || null;
      this.currentManager = payload.currentManager || null;
      // Only members who aren't the outgoing manager and don't already hold MANAGER.
      this.candidates = (payload.candidates || []).filter(
        c =>
          c.uid !== payload.currentManager?.uid &&
          !(c.roleKeys || []).includes("MANAGER")
      );
      this.newUid = null;
      this.reason = "";
      this.errorMessage = "";
      this.isSubmitting = false;
      this.$bvModal.show("SwapManagerDialog");
    },
    close() {
      this.$bvModal.hide("SwapManagerDialog");
    },
    onHide() {
      this.errorMessage = "";
      this.isSubmitting = false;
    },
    extractError(err, fallback) {
      return (
        err.response?.data?.errorMessage?.message ||
        err.response?.data?.message ||
        err.message ||
        fallback
      );
    },
    async submit() {
      if (!this.canSubmit || this.isSubmitting) return;
      this.errorMessage = "";
      this.isSubmitting = true;

      const reason = this.reason.trim();
      const base = `/api/userMgmt/${this.clinicUrl}/branch/${this.branchUrl}/members`;

      // Step 1: assign MANAGER to the new user. Abort if this fails — nothing removed.
      try {
        await axios.post(
          `${base}/${this.newUid}/roles`,
          { roleKey: "MANAGER", reason },
          { skipGlobalErrorHandler: true }
        );
      } catch (err) {
        this.errorMessage = this.extractError(
          err,
          "ไม่สามารถมอบบทบาทผู้จัดการให้ผู้ใช้คนใหม่ได้ (ยังไม่มีการเปลี่ยนแปลง)"
        );
        this.isSubmitting = false;
        return;
      }

      // Step 2: remove MANAGER from the old user. The new manager satisfies the
      // last-MANAGER guard, so this should pass. If it doesn't, the branch is in a
      // safe state (two managers) — surface the error but still refresh the list.
      try {
        await axios.delete(
          `${base}/${this.currentManager.uid}/roles/MANAGER`,
          { data: { reason }, skipGlobalErrorHandler: true }
        );
        this.$emit("swapped", {
          branchUrl: this.branchUrl,
          fromUid: this.currentManager.uid,
          toUid: this.newUid,
        });
        this.close();
      } catch (err) {
        this.errorMessage = this.extractError(
          err,
          "มอบบทบาทผู้จัดการคนใหม่สำเร็จ แต่ถอดบทบาทคนเดิมไม่สำเร็จ กรุณาถอดบทบาทผู้จัดการคนเดิมด้วยตนเอง"
        );
        // New manager was assigned; let the parent refresh to reflect that.
        this.$emit("swapped", {
          branchUrl: this.branchUrl,
          fromUid: this.currentManager.uid,
          toUid: this.newUid,
          partial: true,
        });
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.info-card {
  display: flex;
  align-items: flex-start;
  background: #fff8df;
  border: 1px solid #f0d98a;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
}

.info-card .fas {
  color: #b8860b;
  margin-top: 0.15rem;
}

.manager-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.manager-card.outgoing {
  background: #fdecea;
  border-color: #f3c0bb;
}

.candidate-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 240px;
  overflow-y: auto;
}

.candidate-card {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: 1px solid #dce1e8;
  border-radius: 0.5rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;
}

.candidate-card:hover {
  border-color: #b9c4d4;
}

.candidate-card.selected {
  border-color: #2f7a4d;
  background: #eafaf0;
  box-shadow: 0 0 0 1px #2f7a4d inset;
}

.role-radio {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 3px;
  border: 2px solid #b9c4d4;
  border-radius: 50%;
  display: grid;
  place-items: center;
}

.role-radio.on {
  border-color: #2f7a4d;
}

.role-radio.on::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2f7a4d;
}

.candidate-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.candidate-roles {
  margin-top: 0.25rem;
}
</style>
